import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppContext, useAsyncData } from '@/hooks';
import { api } from '@/api/client';
import { Card } from '@/components/ui/Card';
import { useToast } from '@/components/ui/Toast';
import { Icon } from '@/components/icons';
import styles from './AdminIntegrationsPage.module.css';

interface IntegrationHealth {
  service: string;
  status: string;
  lastSync?: string;
}

const SERVICE_ICONS: Record<string, 'academic' | 'products' | 'users' | 'institution'> = {
  EduCore: 'academic',
  'LMS/LearnHub': 'products',
  'Identity Service': 'users',
  'Payment Gateway': 'institution',
};

const STATUS_COLORS: Record<string, string> = {
  HEALTHY: styles.dotHealthy,
  DEGRADED: styles.dotDegraded,
  DOWN: styles.dotDown,
  NOT_CONFIGURED: styles.dotNotConfigured,
};

const STATUS_LABELS: Record<string, string> = {
  HEALTHY: 'admin.integrations.connected',
  DEGRADED: 'admin.dashboard.degraded',
  DOWN: 'admin.dashboard.down',
  NOT_CONFIGURED: 'admin.integrations.notConfigured',
};

function formatTimestamp(ts: string): string {
  try {
    const d = new Date(ts);
    if (isNaN(d.getTime())) return ts;
    return d.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return ts;
  }
}

export function AdminIntegrationsPage() {
  const { t } = useTranslation();
  const { activeContext } = useAppContext();
  const institutionId = activeContext?.institution?.id ?? '';
  const toast = useToast();
  const [retrying, setRetrying] = useState<Record<string, boolean>>({});

  const { state, reload, replace } = useAsyncData<IntegrationHealth[]>(
    `admin-integrations-${institutionId}`,
    () => api.admin.integrations.list(institutionId),
    !!institutionId,
  );

  const handleRetry = async (service: string) => {
    setRetrying((prev) => ({ ...prev, [service]: true }));
    try {
      const updated = await api.admin.integrations.check(institutionId, service);
      if (state.status === 'ready') {
        replace(state.data.map((svc) => (svc.service === updated.service ? updated : svc)));
      }
      toast.info(t('admin.integrations.checkDone', { status: updated.status }));
    } catch {
      // the card keeps its previous status; the button re-enables for another try
      toast.error(t('admin.integrations.checkFailed'));
    } finally {
      setRetrying((prev) => ({ ...prev, [service]: false }));
    }
  };

  if (state.status === 'loading') {
    return (
      <div className={styles.page}>
        <div className={styles.loading}>{t('common.loading')}</div>
      </div>
    );
  }

  if (state.status === 'error') {
    const error = state.error;
    return (
      <div className={styles.page}>
        <Card className={styles.emptyState}>
          <Icon name={error.isMissingEndpoint ? 'info' : 'error'} size={48} />
          <h2 className={styles.emptyStateTitle}>{t('admin.integrations.title')}</h2>
          <p className={styles.emptyStateBody}>
            {t(error.isMissingEndpoint ? 'platform.failure.missing.body' : 'platform.failure.generic.body')}
          </p>
          <button className={styles.retryButton} onClick={reload}>{t('common.retry')}</button>
        </Card>
      </div>
    );
  }

  const integrations = state.data;

  return (
    <div className={styles.page}>
      <div className="page-header page-headerRow">
        <div>
          <h1>{t('admin.integrations.title')}</h1>
          <p className="page-subtitle">{t('admin.integrations.subtitle')}</p>
        </div>
      </div>

      {integrations.length === 0 ? (
        <p className={`${styles.emptyStateBody} ${styles.noIntegrations}`}>
          {t('admin.integrations.noIntegrations')}
        </p>
      ) : (
        <div className={styles.cardGrid}>
          {integrations.map((svc) => {
            const statusClass = STATUS_COLORS[svc.status] || styles.dotNotConfigured;
            const statusLabelKey = STATUS_LABELS[svc.status] || 'admin.integrations.notConfigured';
            const isRetrying = retrying[svc.service];

            return (
              <Card key={svc.service} className={styles.integrationCard}>
                <div className={styles.cardHeader}>
                  <div className={styles.cardTitleRow}>
                    <div className={styles.cardIcon}>
                      <Icon name={SERVICE_ICONS[svc.service] ?? 'products'} size={20} />
                    </div>
                    <h3 className={styles.cardName}>{svc.service}</h3>
                  </div>
                  <span className={styles.statusIndicator}>
                    <span className={`${styles.statusDot} ${statusClass}`} />
                    {t(statusLabelKey)}
                  </span>
                </div>
                <div className={styles.cardBody}>
                  <span className={styles.lastSync}>
                    {svc.lastSync
                      ? `${t('admin.integrations.lastSync')}: ${formatTimestamp(svc.lastSync)}`
                      : `${t('admin.integrations.lastSync')}: —`}
                  </span>
                  <button
                    type="button"
                    className={styles.retryBtn}
                    disabled={isRetrying}
                    onClick={() => handleRetry(svc.service)}
                  >
                    {isRetrying ? '...' : t('admin.integrations.retry')}
                  </button>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
